export type IdentifierCasing = 'title' | 'sentence' | 'none';

/**
 * Normalize an identifier into spaced words.
 * Handles camelCase, PascalCase, acronyms, snake_case, and kebab-case.
 *
 * @example
 * splitIdentifierWords('peopleIncident') // 'people Incident'
 * splitIdentifierWords('ITSystemsNetwork') // 'IT Systems Network'
 * splitIdentifierWords('service_quality-contract') // 'service quality contract'
 */
function splitIdentifierWords(value: string) {
    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Convert an identifier into a human-readable label.
 * Defaults to title case and supports sentence case and raw normalized output.
 *
 * @example
 * humanizeIdentifier('peopleIncident') // 'People Incident'
 * humanizeIdentifier('type_ofIncident', { casing: 'sentence' }) // 'Type of Incident'
 * humanizeIdentifier('type_ofIncident', { casing: 'none' }) // 'type of Incident'
 */
export function humanizeIdentifier(
    value: string | null | undefined,
    options: { casing?: IdentifierCasing } = {},
): string {
    if (!value) return '';

    const normalized = splitIdentifierWords(value);
    const casing = options.casing ?? 'title';

    if (casing === 'none') return normalized;
    if (casing === 'sentence') return normalized.replace(/^\w/, (char) => char.toUpperCase());

    return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function isIdentifierLikeString(
    value: string | null | undefined,
    options: { requireLeadingLetter?: boolean } = {},
): boolean {
    if (!value) return false;

    const trimmed = value.trim();
    if (!trimmed) return false;

    const pattern = options.requireLeadingLetter ? /^[A-Za-z][A-Za-z0-9_-]*$/ : /^[A-Za-z0-9_-]+$/;
    return pattern.test(trimmed);
}

/**
 * Convert an identifier to kebab-case.
 * Useful for CSS/icon keys derived from enum or type names.
 *
 * @example
 * identifierToKebab('PeopleIncident') // 'people-incident'
 * identifierToKebab('ITSystemsNetwork') // 'it-systems-network'
 * identifierToKebab('service_quality-contract') // 'service-quality-contract'
 */
export function identifierToKebab(value: string | null | undefined): string {
    if (!value) return '';

    return splitIdentifierWords(value).toLowerCase().replace(/\s+/g, '-');
}
